import React, { useState } from 'react';

const DungeonShowcase = () => {
  const features = [
    { name: 'Vector Field Pathfinding', icon: '🧭', desc: 'Smooth AI movement using field-based navigation' },
    { name: 'Raycasting System', icon: '👁️', desc: 'Line-of-sight detection and dynamic lighting' },
    { name: 'Physics Engine', icon: '⚡', desc: 'Custom gravity, acceleration, and collision' },
    { name: 'Combat System', icon: '⚔️', desc: 'Melee, ranged weapons, and status effects' },
    { name: 'Dynamic Camera', icon: '📷', desc: 'Smart camera with minimap integration' },
    { name: 'Boss Battles', icon: '👹', desc: 'Challenging encounters with unique mechanics' }
  ];

  const teamMembers = [
    { name: 'Saurav Singh Chandel', role: 'Developer' },
    { name: 'Jenna Hillier', role: 'Developer' },
    { name: 'Arunav Saha', role: 'Developer' },
    { name: 'Adrian Alarcon', role: 'Developer' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-red-900/20 to-gray-900 text-white">
      {/* Hero Section */}
      <header className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-96 h-96 bg-red-500/10 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-80 h-80 bg-orange-500/10 rounded-full filter blur-3xl"></div>
        </div>

        <div className="relative max-w-6xl mx-auto px-6 text-center">
          <div className="inline-block mb-4 px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-full">
            <span className="text-red-300 text-sm font-medium">🎮 CS4300: Game Development</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold mb-6">
            <span className="bg-gradient-to-r from-red-500 via-orange-500 to-amber-500 bg-clip-text text-transparent">
              Dungeon of the Wild
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto mb-8">
            Action-adventure RPG with custom-built maps, strategic combat, 
            and AI-driven enemies—built from scratch in C++ and SFML
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <a
              href="https://github.com/ARNAB814/Dungeon_of_The_Wild"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-gray-800 hover:bg-gray-700 border border-gray-600 px-6 py-3 rounded-lg transition-all"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.163 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.342-3.369-1.342-.454-1.155-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.337 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.16 22 16.42 22 12c0-5.523-4.477-10-10-10z" />
              </svg>
              <span>View Source Code</span>
            </a>
            <a
              href="https://www.youtube.com/watch?v=OAiTauOFOWk"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-red-600 hover:bg-red-500 px-6 py-3 rounded-lg transition-all shadow-lg shadow-red-500/30"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              <span>Watch Trailer</span>
            </a>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6">
              <div className="text-3xl font-bold text-red-400">7,564</div>
              <div className="text-gray-400 text-sm">Lines of Code</div>
            </div>
            <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6">
              <div className="text-3xl font-bold text-orange-400">C++</div>
              <div className="text-gray-400 text-sm">Built From Scratch</div>
            </div>
            <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6">
              <div className="text-3xl font-bold text-amber-400">SFML</div>
              <div className="text-gray-400 text-sm">Graphics Library</div>
            </div>
            <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6">
              <div className="text-3xl font-bold text-yellow-400">60</div>
              <div className="text-gray-400 text-sm">FPS Target</div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-16">
        {/* Game Overview */}
        <section className="mb-20">
          <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-2xl p-8">
            <h2 className="text-3xl font-bold text-red-400 mb-6">The Game</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4 text-gray-300">
                <p>
                  Dungeon of the Wild is an action-adventure RPG where players explore custom-built 
                  overworld and dungeon maps, battling various enemies and challenging powerful bosses.
                </p>
                <p>
                  Built entirely from scratch using C++ and SFML, the game features advanced systems 
                  including vector field pathfinding for enemy AI, raycasting for line-of-sight detection, 
                  physics-driven movement, and real-time shadow effects.
                </p>
                <p>
                  Players can utilize multiple weapons (sword, arrows, beam), dash with invincibility frames, 
                  and experience various status effects throughout their adventure.
                </p>
              </div>
              <div className="bg-gray-900/50 rounded-xl p-6">
                <h4 className="text-lg font-bold text-white mb-4">Combat Features</h4>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-center">
                    <span className="text-red-400 mr-2">⚔️</span>
                    <span>Melee sword combat with combos</span>
                  </li>
                  <li className="flex items-center">
                    <span className="text-orange-400 mr-2">🏹</span>
                    <span>Ranged arrow attacks</span>
                  </li>
                  <li className="flex items-center">
                    <span className="text-amber-400 mr-2">✨</span>
                    <span>Beam special ability</span>
                  </li>
                  <li className="flex items-center">
                    <span className="text-yellow-400 mr-2">💨</span>
                    <span>Dash with i-frames</span>
                  </li>
                  <li className="flex items-center">
                    <span className="text-green-400 mr-2">💚</span>
                    <span>Health and status effects</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Technical Features */}
        <section className="mb-20">
          <h2 className="text-3xl font-bold text-center mb-12">Technical Implementation</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={feature.name}
                className="bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-xl p-6 hover:border-red-500/50 transition-all group"
              >
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform">{feature.icon}</div>
                <h3 className="text-lg font-bold text-white mb-2">{feature.name}</h3>
                <p className="text-gray-400 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* AI System Deep Dive */}
        <section className="mb-20">
          <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-red-400 mb-6">Enemy AI System</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-bold text-white mb-4">Vector Field Pathfinding</h3>
                <p className="text-gray-300 mb-4">
                  Instead of traditional A* pathfinding, enemies use a vector field system that 
                  calculates optimal movement directions for every point in the map. This creates 
                  smooth, natural-looking enemy movement toward the player.
                </p>
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <code className="text-green-400 text-sm font-mono">
                    // Vector field updates in real-time<br/>
                    // Enemies follow gradient to player<br/>
                    // Handles obstacles automatically
                  </code>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-4">Raycasting & Visibility</h3>
                <p className="text-gray-300 mb-4">
                  Custom raycasting system determines line-of-sight between entities. Enemies 
                  only pursue players they can see, creating tactical gameplay opportunities 
                  and enabling stealth mechanics.
                </p>
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <code className="text-green-400 text-sm font-mono">
                    // Cast rays from enemy to player<br/>
                    // Check for wall intersections<br/>
                    // Enable/disable pursuit behavior
                  </code>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Physics Engine */}
        <section className="mb-20">
          <h2 className="text-3xl font-bold text-center mb-12">Custom Physics Engine</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </div>
              <h3 className="font-bold text-white mb-2">Gravity System</h3>
              <p className="text-gray-400 text-sm">Realistic falling and jumping with configurable gravity constants</p>
            </div>
            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="font-bold text-white mb-2">Acceleration</h3>
              <p className="text-gray-400 text-sm">Smooth velocity changes for natural movement feel</p>
            </div>
            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-bold text-white mb-2">Collision Detection</h3>
              <p className="text-gray-400 text-sm">AABB collision system with proper resolution</p>
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="mb-20">
          <h2 className="text-3xl font-bold text-center mb-12">Development Team</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {teamMembers.map((member, index) => (
              <div 
                key={member.name}
                className="bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-xl p-6 text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-orange-500 rounded-full mx-auto mb-4 flex items-center justify-center text-2xl font-bold">
                  {member.name.charAt(0)}
                </div>
                <h4 className="font-semibold text-white">{member.name}</h4>
                <p className="text-gray-400 text-sm">{member.role}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Course Info */}
        <section>
          <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-2xl p-8">
            <h3 className="text-xl font-bold text-red-400 mb-6">Project Details</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-gray-400 text-sm mb-1">Course</p>
                <p className="text-white font-medium">CS4300: Game Development</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm mb-1">Institution</p>
                <p className="text-white font-medium">Memorial University of Newfoundland</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm mb-1">Completion</p>
                <p className="text-white font-medium">April 2024</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm mb-1">Codebase</p>
                <p className="text-white font-medium">7,564 lines of C++</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-black/30 border-t border-gray-800 py-8">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-gray-400 mb-4">
            Dungeon of the Wild — CS4300 Game Development Final Project
          </p>
          <div className="flex justify-center gap-6">
            <a
              href="https://github.com/ARNAB814/Dungeon_of_The_Wild"
              target="_blank"
              rel="noopener noreferrer"
              className="text-red-400 hover:text-red-300 transition-colors"
            >
              Source Code →
            </a>
            <a
              href="https://www.youtube.com/watch?v=OAiTauOFOWk"
              target="_blank"
              rel="noopener noreferrer"
              className="text-orange-400 hover:text-orange-300 transition-colors"
            >
              Watch Trailer →
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DungeonShowcase;
